import { z } from "zod";

import { CrmDealCreationResultSchema } from "./crmDealCreation";
import { FieldMappingAiConfigSchema, FieldMappingSourceType } from "./hubspot";

export type { AiTestRequest, AiTestResponse, FieldMappingAiConfig } from "./hubspot";
export { AiTestResponseSchema } from "./hubspot";

// Entity types for field mappings
export enum ZohoEntityType {
	LEAD = "LEAD",
	CONTACT = "CONTACT",
	ACCOUNT = "ACCOUNT",
	DEAL = "DEAL",
}

// Re-export the shared source type enum so zoho consumers don't need to import from the hubspot schema.
export { FieldMappingSourceType };

// Zoho CRM field from API
export const ZohoFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string(),
	isCustom: z.boolean().optional(),
	description: z.string().optional(),
	options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
	isRequired: z.boolean().optional(),
	isReadOnly: z.boolean().optional(),
	isCalculated: z.boolean().optional(),
	isMulti: z.boolean().optional(),
});

export type ZohoField = z.infer<typeof ZohoFieldSchema>;

// Source field from our system
export const ZohoSourceFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string().optional(),
	category: z.string().optional(),
});

export type ZohoSourceField = z.infer<typeof ZohoSourceFieldSchema>;

// Field mapping
export const ZohoFieldMappingSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(ZohoEntityType),
	sourceField: z.string(),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	zohoField: z.string(),
	zohoFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
	/** When Zoho last refused to search this mapped field (PREC-4354); null once it answers. */
	searchRefusedAt: z.string().nullable().optional(),
	searchRefusalReason: z.string().nullable().optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type ZohoFieldMapping = z.infer<typeof ZohoFieldMappingSchema>;

// Create field mapping DTO
export const CreateZohoFieldMappingSchema = z.object({
	entityType: z.nativeEnum(ZohoEntityType),
	sourceField: z.string().min(1),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	zohoField: z.string().min(1),
	zohoFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type CreateZohoFieldMapping = z.infer<typeof CreateZohoFieldMappingSchema>;

// Update field mapping DTO
export const UpdateZohoFieldMappingSchema = z.object({
	sourceField: z.string().optional(),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	zohoField: z.string().optional(),
	zohoFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type UpdateZohoFieldMapping = z.infer<typeof UpdateZohoFieldMappingSchema>;

// Bulk create mappings
export const BulkCreateZohoFieldMappingsSchema = z.object({
	mappings: z.array(CreateZohoFieldMappingSchema),
});

export type BulkCreateZohoFieldMappings = z.infer<typeof BulkCreateZohoFieldMappingsSchema>;

// Field mapping preview (dry run against a real prospect, no Zoho write)
export const ZohoFieldMappingPreviewRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.nativeEnum(ZohoEntityType),
});

export type ZohoFieldMappingPreviewRequest = z.infer<typeof ZohoFieldMappingPreviewRequestSchema>;

export const ZohoFieldMappingPreviewRowSchema = z.object({
	mappingId: z.number(),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	sourceField: z.string(),
	zohoField: z.string(),
	zohoFieldLabel: z.string().optional(),
	isActive: z.boolean(),
	value: z.string().nullable(),
	resolved: z.boolean(),
	isAiMapping: z.boolean(),
});

export type ZohoFieldMappingPreviewRow = z.infer<typeof ZohoFieldMappingPreviewRowSchema>;

export const ZohoFieldMappingPreviewResponseSchema = z.object({
	rows: z.array(ZohoFieldMappingPreviewRowSchema),
	fallbacks: z.array(z.object({ zohoField: z.string(), value: z.string() })),
});

export type ZohoFieldMappingPreviewResponse = z.infer<typeof ZohoFieldMappingPreviewResponseSchema>;

// Test sync (real write): runs the real sync path for one prospect; entityType
// selects which mappings are exercised (LEAD default, CONTACT/ACCOUNT force the
// Contact+Account path, DEAL creates a deal).
export const ZohoTestSyncRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.nativeEnum(ZohoEntityType).optional(),
});

export type ZohoTestSyncRequest = z.infer<typeof ZohoTestSyncRequestSchema>;

export const ZohoTestSyncResponseSchema = z.object({
	success: z.boolean(),
	error: z.string().nullish(),
	zohoLeadId: z.string().optional(),
	zohoContactId: z.string().optional(),
	zohoAccountId: z.string().optional(),
	zohoDealId: z.string().optional(),
	recordUrl: z.string().nullish(),
});

export type ZohoTestSyncResponse = z.infer<typeof ZohoTestSyncResponseSchema>;

// API Responses
export const ZohoFieldsResponseSchema = z.object({
	fields: z.array(ZohoFieldSchema),
});

export type ZohoFieldsResponse = z.infer<typeof ZohoFieldsResponseSchema>;

export const ZohoSourceFieldsResponseSchema = z.object({
	contactFields: z.array(ZohoSourceFieldSchema),
	accountFields: z.array(ZohoSourceFieldSchema),
	dealFields: z.array(ZohoSourceFieldSchema).optional(),
	leadFields: z.array(ZohoSourceFieldSchema).optional(),
});

export type ZohoSourceFieldsResponse = z.infer<typeof ZohoSourceFieldsResponseSchema>;

export const ZohoFieldMappingsResponseSchema = z.array(ZohoFieldMappingSchema);

export type ZohoFieldMappingsResponse = z.infer<typeof ZohoFieldMappingsResponseSchema>;

// Sync Trigger Types
export enum ZohoSyncTrigger {
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export enum ZohoSyncTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

// Company Sync Trigger Response
export const ZohoCompanySyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(ZohoSyncTrigger),
});

export type ZohoCompanySyncTriggerResponse = z.infer<typeof ZohoCompanySyncTriggerResponseSchema>;

// List Sync Trigger Response
export const ZohoListSyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(ZohoSyncTriggerWithInherit),
	effectiveTrigger: z.nativeEnum(ZohoSyncTrigger),
	isInherited: z.boolean(),
});

export type ZohoListSyncTriggerResponse = z.infer<typeof ZohoListSyncTriggerResponseSchema>;

// Update Sync Trigger DTOs
export const UpdateZohoCompanySyncTriggerSchema = z.object({
	trigger: z.nativeEnum(ZohoSyncTrigger),
});

export type UpdateZohoCompanySyncTrigger = z.infer<typeof UpdateZohoCompanySyncTriggerSchema>;

export const UpdateZohoListSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(ZohoSyncTriggerWithInherit),
});

export type UpdateZohoListSyncTrigger = z.infer<typeof UpdateZohoListSyncTriggerSchema>;

// Sync Log
export const ZohoSyncLogSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(ZohoEntityType),
	status: z.enum(["PENDING", "IN_PROGRESS", "SUCCESS", "PARTIAL", "FAILED"]),
	recordsSynced: z.number(),
	recordsFailed: z.number(),
	errorMessage: z.string().optional(),
	createdAt: z.string(),
	completedAt: z.string().optional(),
});

export type ZohoSyncLog = z.infer<typeof ZohoSyncLogSchema>;

export const ZohoSyncLogsResponseSchema = z.array(ZohoSyncLogSchema);

export type ZohoSyncLogsResponse = z.infer<typeof ZohoSyncLogsResponseSchema>;

// Deal Creation Trigger Types
export enum ZohoDealCreationTrigger {
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

// Company Deal Creation Trigger Response
export const ZohoCompanyDealCreationTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(ZohoDealCreationTrigger),
});

export type ZohoCompanyDealCreationTriggerResponse = z.infer<
	typeof ZohoCompanyDealCreationTriggerResponseSchema
>;

// Update Company Deal Creation Trigger DTO
export const UpdateZohoCompanyDealCreationTriggerSchema = z.object({
	trigger: z.nativeEnum(ZohoDealCreationTrigger),
});

export type UpdateZohoCompanyDealCreationTrigger = z.infer<
	typeof UpdateZohoCompanyDealCreationTriggerSchema
>;

// Zoho Pipeline Stage
export const ZohoPipelineStageSchema = z.object({
	id: z.string(),
	label: z.string(),
	displayOrder: z.number().optional(),
});

export type ZohoPipelineStage = z.infer<typeof ZohoPipelineStageSchema>;

// Zoho Pipeline
export const ZohoPipelineSchema = z.object({
	id: z.string(),
	label: z.string(),
	stages: z.array(ZohoPipelineStageSchema),
});

export type ZohoPipeline = z.infer<typeof ZohoPipelineSchema>;

export const ZohoPipelinesResponseSchema = z.object({
	pipelines: z.array(ZohoPipelineSchema),
});

export type ZohoPipelinesResponse = z.infer<typeof ZohoPipelinesResponseSchema>;

// Zoho Owner
export const ZohoOwnerSchema = z.object({
	id: z.string(),
	email: z.string(),
	fullName: z.string().optional(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
});

export type ZohoOwner = z.infer<typeof ZohoOwnerSchema>;

export const ZohoOwnersResponseSchema = z.object({
	owners: z.array(ZohoOwnerSchema),
});

export type ZohoOwnersResponse = z.infer<typeof ZohoOwnersResponseSchema>;

// Deal Configuration (simpler than HubSpot — no lifecycle stages or lead statuses)
export const ZohoDealConfigSchema = z.object({
	defaultPipeline: z.string().optional().nullable(),
	defaultDealStage: z.string().optional().nullable(),
	dealNameTemplate: z.string().optional().nullable(),
	closeDateOffset: z.number().optional().nullable(),
	defaultOwner: z.string().optional().nullable(),
	autoCreateCampaigns: z.boolean().optional(),
});

export type ZohoDealConfig = z.infer<typeof ZohoDealConfigSchema>;

export const ZohoDealConfigResponseSchema = ZohoDealConfigSchema;

export type ZohoDealConfigResponse = z.infer<typeof ZohoDealConfigResponseSchema>;

// User Mapping (Signalist user → Zoho owner)
export const ZohoUserMappingSchema = z.object({
	id: z.number(),
	userId: z.number(),
	zohoOwnerId: z.string(),
	zohoOwnerName: z.string().optional(),
	userFirstName: z.string().optional(),
	userLastName: z.string().optional(),
	userEmail: z.string().optional(),
});

export type ZohoUserMapping = z.infer<typeof ZohoUserMappingSchema>;

export const ZohoUserMappingsResponseSchema = z.array(ZohoUserMappingSchema);

export type ZohoUserMappingsResponse = z.infer<typeof ZohoUserMappingsResponseSchema>;

// Create a single user mapping
export const CreateZohoUserMappingSchema = z.object({
	userId: z.number(),
	zohoOwnerId: z.string(),
	zohoOwnerName: z.string().optional(),
});

export type CreateZohoUserMapping = z.infer<typeof CreateZohoUserMappingSchema>;

// Create Zoho Deal
export const CreateZohoDealRequestSchema = z.object({
	prospectId: z.number(),
	dealName: z.string().optional(),
	pipeline: z.string().optional(),
	dealStage: z.string().optional(),
	dealOwnerId: z.string().optional(),
	closeDate: z.string().optional(),
});

export type CreateZohoDealRequest = z.infer<typeof CreateZohoDealRequestSchema>;

export const CreateZohoDealResponseSchema = CrmDealCreationResultSchema;

export type CreateZohoDealResponse = z.infer<typeof CreateZohoDealResponseSchema>;

// Conversation Sync
export const ZohoConversationSyncResponseSchema = z.object({
	enabled: z.boolean(),
});

export type ZohoConversationSyncResponse = z.infer<typeof ZohoConversationSyncResponseSchema>;

export const UpdateZohoConversationSyncSchema = z.object({
	enabled: z.boolean(),
});

export type UpdateZohoConversationSync = z.infer<typeof UpdateZohoConversationSyncSchema>;

// OAuth
export const ZohoOAuthConnectResponseSchema = z.object({
	url: z.string(),
});

export type ZohoOAuthConnectResponse = z.infer<typeof ZohoOAuthConnectResponseSchema>;

export const ZohoOAuthStatusResponseSchema = z.object({
	connected: z.boolean(),
	zohoOrgId: z.string().optional(),
	tokenExpiry: z.string().optional(),
	/** Zoho refused to refresh the stored sign-in; the company must reconnect (PREC-4353). */
	needsReauthorization: z.boolean().optional(),
});

export type ZohoOAuthStatusResponse = z.infer<typeof ZohoOAuthStatusResponseSchema>;

export const ZohoOAuthDisconnectResponseSchema = z.object({
	success: z.boolean(),
});

export type ZohoOAuthDisconnectResponse = z.infer<typeof ZohoOAuthDisconnectResponseSchema>;

// Manual Resync
export const ZohoResyncResponseSchema = z.object({
	queued: z.boolean(),
	queuedCount: z.number(),
	companyId: z.number(),
	triggeredAt: z.string(),
});

export type ZohoResyncResponse = z.infer<typeof ZohoResyncResponseSchema>;

// Campaign Links
export const ZohoCampaignLinkSchema = z.object({
	id: z.number(),
	listId: z.number(),
	zohoCampaignId: z.string(),
	zohoCampaignName: z.string().optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type ZohoCampaignLink = z.infer<typeof ZohoCampaignLinkSchema>;

export const ZohoCampaignSchema = z.object({
	id: z.string(),
	name: z.string(),
});

export type ZohoCampaign = z.infer<typeof ZohoCampaignSchema>;

export const ZohoCampaignsResponseSchema = z.array(ZohoCampaignSchema);

export type ZohoCampaignsResponse = z.infer<typeof ZohoCampaignsResponseSchema>;

export const ZohoCampaignLinksResponseSchema = z.array(ZohoCampaignLinkSchema);

export type ZohoCampaignLinksResponse = z.infer<typeof ZohoCampaignLinksResponseSchema>;
